import { ISelectOption } from '@private/components';

import { ITLLimitForm } from 'components/LimitFormSidebar/types';
import { IGroupLimitEditData } from 'routes/pages/AuthorizedStack/PspManagement/TerminalLinksLimits/GroupEdit/helpers';
import { TAllSupportedTypes } from 'routes/pages/AuthorizedStack/PspManagement/TerminalsLinks/TerminalLink/View/RequiredParamsForm/helpers';
import { IAllPageFilters, INullable, TObject, TSearch } from 'utils/types';

import { CALCULATION_VARIANTS, LIMIT_TYPE } from '../data/constants';
import { OPERATION_TYPE } from '../paymentMethods/constants';
import {
  EXIT_IFRAME_MODE,
  TRANSACTION_STATUS,
  TRANSACTIONS_STATE,
  TRANSACTIONS_TYPE,
} from '../transactions/constants';

import {
  CARDHOLDER_OVERRIDE_TYPES,
  COPIED_LIMIT_STATUS,
  COPY_LIMITS_SOURCE_TYPE,
  COPY_LIMITS_TARGET_STATUS,
  COPY_LIMITS_TARGET_TYPE,
  COUNT_OF,
  EXISTING_LIMITS_MODE,
  ISSUING_ORG_OPERATION,
  UNIQUE_CUSTOMER_ID_TYPES,
} from './constants';

export enum TERMINAL_LINK_HEALTH_STATUS {
  INSUFFICIENT = 'INSUFFICIENT',
  NO_DATA = 'NO_DATA',
  OK = 'OK',
  UNAVAILABLE = 'UNAVAILABLE',
  WARMING_UP = 'WARMING_UP',
}

export interface ICopyTLLimitsForm {
  copiedStatus: COPIED_LIMIT_STATUS;
  existingLimitsMode: EXISTING_LIMITS_MODE;
  limitIds: number[];
  sourceId: INullable<number>;
  sourceMerchantId: INullable<number>;
  sourceMerchantTerminalId: INullable<number>;
  sourceType: COPY_LIMITS_SOURCE_TYPE;
  targetIds: number[];
  targetMerchantId: INullable<number>;
  targetMerchantTerminalId: INullable<number>;
  targetPaymentMethod: INullable<string>;
  targetType: COPY_LIMITS_TARGET_TYPE;
}

export interface ICopyTLLimitsPayload {
  copiedStatus: COPIED_LIMIT_STATUS;
  dryRun: boolean;
  existingLimitsMode: EXISTING_LIMITS_MODE;
  limitIds: number[];
  sourceId: number;
  sourceType: COPY_LIMITS_SOURCE_TYPE;
  targetIds: number[];
  targetType: COPY_LIMITS_TARGET_TYPE;
}

export interface ICopyTLLimitsResult {
  targets: ICopyTLLimitsTarget[];
  totals: ICopyTLLimitsTotals;
}

export interface ICopyTLLimitsTarget {
  copied: number[];
  notCopiedCurrency: number[];
  notCopiedInvalidSource: number[];
  skipped: number[];
  status: COPY_LIMITS_TARGET_STATUS;
  targetId: number;
  warnings: ICopyTLLimitsWarning[];
}

export interface ICopyTLLimitsTotals {
  copied: number;
  notCopiedCurrency: number;
  notCopiedInvalidSource: number;
  skipped: number;
  warnings: number;
}

export interface ICopyTLLimitsWarning {
  code: string;
  limitId: number;
  params: Record<string, number | string>;
}

export interface ICreateRequiredParametersForm {
  id: string;
  parameters: IRequiredParameter[];
  transactionType: TAllSupportedTypes;
}

export interface ICreateTerminalLinkPayload extends ITerminalLinkBase {
  bankTerminalId: null | number;
  merchantTerminalIds: INullable<number[]>;
}

export interface IDeleteTLSettlementConfigs {
  search?: TSearch;
}

export interface IDropDownValue {
  id: number;
  name: INullable<string>;
  value: INullable<string>;
}

export interface IEditTerminalLinkPayload {
  id: string;
  payload: ITerminalLinkBase;
}

export interface IGetAllRelatedEntitiesPayload {
  filters: IAllPageFilters;
  id: string;
}

export interface IGetTerminalLinksConfig {
  disabledText?: string;
  enabledText?: string;
  showWithId?: 'after' | 'before';
  showWithMerchant?: boolean;
  showWithStateFlag?: boolean; // enabled/disabled
}

export interface IGetTerminalLinksPayload extends IGetTerminalLinksConfig {
  enabled?: boolean;
  merchantId?: INullable<number>;
  merchantTerminalId?: INullable<number | string>;
  onlyMoto?: boolean;
  operationType?: OPERATION_TYPE;
  paymentMethod?: INullable<string>;
  terminalLinkEnabled?: boolean;
  terminalLinkGroupId?: INullable<number>;
  terminalLinkLimitsEnabled?: boolean;
}

export interface IGetTLGroupTerminalLinksPayload {
  enabled?: boolean;
  merchantId?: INullable<number>;
  showWithId?: 'after' | 'before';
  terminalLinkEnabled?: boolean;
  terminalLinkGroupId?: INullable<number>;
  terminalLinkLimitsEnabled?: boolean;
}

export interface IGetTLGroupTerminalLinksResult {
  terminalLinks: ITrustedSelectOption[];
  terminalLinksMap: Record<number, ITrustedSelectOption>;
}

export interface IGetTLParameterLowerLevelValuesPayload {
  bankParameterId: number;
  terminalLinkId: string;
}

export interface IGroupEditRequiredParametersForm {
  groupEditMode?: string;
  parameters: IRequiredParameter[];
  terminalLinkSearch: string;
  transactionType: TAllSupportedTypes;
}

export interface IGroupEditTLLimitsPayload {
  filters: IAllPageFilters;
  settings: IGroupLimitEditData;
}

export interface IGroupEditTLParametersPayload {
  filters: IAllPageFilters;
  settings: Record<string, string>;
}

export interface ILocalDropDownValue {
  locale: INullable<string>;
  value: INullable<string>;
}

export interface IModifyRequiredParameter {
  forcedHash: INullable<number>;
  forcedOverride: INullable<string>;
  forcedOverrideType: INullable<string>;
  forcedPreparePage: INullable<string>;
  overrideIfNotNull: INullable<string>;
  overrideIfNull: INullable<string>;
  truncateIfNotValidOption: INullable<string>;
  truncateIfNotValidValue: INullable<number>;
}

export interface IRequiredParameter extends IModifyRequiredParameter {
  dropDownValues: IDropDownValue[];
  inputClass: INullable<string>;
  inputType: INullable<string>;
  localInputValidation: INullable<ILocalValidation>;
  localInputValidationMessage: INullable<ILocalValidation>;
  localPlaceholder: ILocalValidation;
  name: INullable<string>;
  order: number;
  parameterType: INullable<string>;
  type: string;
}

export type IRequiredParameterData = TObject & {
  localInputValidation: ILocaleDropDownValues;
  localInputValidationMessage: ILocaleDropDownValues;
  localPlaceholder: ILocaleDropDownValues;
  values: IDropDownValue[];
};

export interface IRequiredParameters {
  result: IRequiredParameterView[];
}

export interface IRequiredParametersData {
  result: IRequiredParameterData[];
}

export interface IRequiredParameterView extends IRequiredParameter {
  id: number;
  transactionType: TRANSACTIONS_TYPE;
}

export interface ISoftResetTLParameterPayload {
  id: string;
  payload: {
    bankParameterId: number;
    value: string;
  };
}

export interface ITerminalGroup {
  enabled: boolean;
  id: number;
  limitsCount?: number;
  merchant: {
    id: number;
    name: string;
  };
  name: string;
  terminalLinks?: number[];
  terminalLinksCount?: number;
}

export type ITerminalGroupData = TObject & {
  merchant?: TObject;
};

export interface ITerminalGroupLimits extends ITerminalLinkLimit {
  groupId?: number;
}

export interface ITerminalLink extends ITerminalLinkBase {
  bankTerminal: {
    bank: {
      adapterCode: string;
      id: number;
      name: string;
      paymentMethod: string;
    };
    id: number;
    name: string;
  };
  cardholderOverrideType: CARDHOLDER_OVERRIDE_TYPES;
  id: number;
  merchantTerminal: {
    id: number;
    merchant: {
      id: number;
      name: string;
    };
    name: string;
  };
  parameters: ITerminalLinkParameter[];
  paymentMethod: string;
  settlementContract: {
    contractName: string;
    id: number;
  };
  terminalLinkGroups: ITerminalLinkGroup[];
}

export interface ITerminalLinkAction {
  accuracyPercentage: INullable<number>;
  actionType: INullable<string>;
  created: INullable<string>;
  enabled: boolean;
  id: INullable<number>;
  name: INullable<string>;
  outputChannels: {
    id: INullable<number>;
    name: INullable<string>;
  }[];
  stage: INullable<string>;
  terminalLink: {
    id: INullable<number>;
  };
  triggerType: INullable<string>;
  updated: INullable<string>;
}

export interface ITerminalLinkBase {
  cardholderOverrideMinLength: INullable<number | string>;
  cardholderOverrideType: INullable<CARDHOLDER_OVERRIDE_TYPES>;
  checkStatusOnEarlyWebhook: boolean;
  checkStatusOnEarlyWebhookDelaySeconds: INullable<number | string>;
  customParametersDeposit: INullable<string>;
  customParametersPayout: INullable<string>;
  description: INullable<string>;
  enabled: boolean;
  exitIFrame: boolean;
  exitIFrameMode: EXIT_IFRAME_MODE;
  limitsEnabled: boolean;
  motoEnabled: boolean;
  multipleFinalStates: boolean;
  payoutTimeout: INullable<number>;
  proxyUrl: INullable<string>;
  pspSendPaymentTimeoutSeconds: INullable<number>;
  purchaseTimeout: INullable<number>;
  recurringEnabled: boolean;
  refundTimeout: INullable<number>;
  subPaymentMethod: INullable<string>;
  threeDSEnabled: boolean;
  trusted: boolean;
  uniqueCustomerId: UNIQUE_CUSTOMER_ID_TYPES;
  useBankMessage: boolean;
  useCustomActions: boolean;
  useCustomReturnUrl: boolean;
  webhookIpWhitelistEnabled: boolean;
  webhookProxyUrl: INullable<string>;
  webhooksEnabled: boolean;
  whitelistIps: INullable<(null | string)[]>;
}

export interface ITerminalLinkCopyForm {
  merchantTerminalIds: number[];
  terminalLinkIds: number[];
  withGroupLimits: boolean;
  withParameters: boolean;
}

export type ITerminalLinkData = TObject & {
  bankTerminal: TObject & {
    bank: TObject;
  };
  merchantTerminal: TObject & {
    merchant: TObject;
  };
  parameters: ITerminalLinkParameter[];
  settlementContract: TObject;
  terminalLinkGroups: ITerminalLinkGroup[];
};

export interface ITerminalLinkGroup {
  id: number;
  name: string;
}

export interface ITerminalLinkHealth {
  baseline: ITerminalLinkHealthBlock;
  day: ITerminalLinkHealthBlock;
  recent: ITerminalLinkHealthBlock;
  terminalLinkId: number;
}

export interface ITerminalLinkHealthBlock {
  approvalRate?: number;
  asOf: string;
  counts?: ITerminalLinkHealthCounts;
  lastAttemptAt?: string;
  latency?: {
    maxMs: number;
    meanMs: number;
  };
  parameters: {
    minSamples: number;
    windowMinutes: number;
  };
  status: TERMINAL_LINK_HEALTH_STATUS;
  windowFrom: string;
  windowTo: string;
}

export interface ITerminalLinkHealthCounts {
  abandoned: number;
  approved: number;
  attemptsStarted: number;
  declined: number;
  latencySamples: number;
  nonAttributable: number;
  pending: number;
  samples: number;
  timedOut: number;
}

export type ITerminalLinkHealthData = TObject & {
  baseline: TObject;
  day: TObject;
  recent: TObject;
};

export interface ITerminalLinkLimit {
  actualAmount?: number;
  actualCount?: number;
  amount?: number;
  cardBrand: string;
  count?: number;
  countOf: COUNT_OF;
  currency: string;
  customPeriod: INullable<number>;
  enabled: boolean;
  id?: number;
  issuingOrg: INullable<ITLLimitIssuingOrg>;
  lastUpdated: string;
  limitType: LIMIT_TYPE;
  periodCalculationType: CALCULATION_VARIANTS;
  periodOffset?: string;
  periodType: string;
  transactionStatus: TRANSACTION_STATUS;
  transactionType: string;
  uniqueCustomer: boolean;
  uniqueCustomerId: UNIQUE_CUSTOMER_ID_TYPES;
  uniqueInstrument: boolean;
}

export interface ITerminalLinkParameter {
  dataType: string;
  id: number;
  name: string;
  value: string;
}

export interface ITerminalLinksGroups {
  enabled: boolean;
  id: string;
  limitsCount?: number;
  merchantId: number;
  merchantName: string;
  name: string;
  terminalLinksCount?: number;
}

export interface ITerminalLinksLimits extends ITerminalLinkLimit {
  terminalLink: {
    bankTerminal: {
      bank: {
        id: number;
        name: string;
        paymentMethod: string;
      };
      id: number;
      name: string;
    };
    description: string;
    id: number;
    merchantTerminal: {
      id: number;
      merchant: {
        id: number;
        name: string;
      };
      name: string;
    };
  };
}

export type ITerminalLinksLimitsData = TObject & {
  terminalLink: TObject & {
    bankTerminal: TObject & {
      bank: TObject;
    };
    merchantTerminal: TObject & {
      merchant: TObject;
    };
  };
};

export interface ITerminalLinksParameters {
  bankId?: number;
  bankName: string;
  bankTerminalId?: number;
  bankTerminalName: string;
  comment?: string;
  dataType: string;
  defaultValue: string;
  editable: boolean;
  level: string;
  merchantId?: number;
  merchantName: string;
  merchantTerminalId?: number;
  merchantTerminalName: string;
  name: string;
  paramId: number;
  paymentMethod: string;
  terminalLinkDescription: string;
  terminalLinkId?: number;
  terminalLinkParamValueId: number;
  value: string;
}

export interface ITerminalsLinks {
  bankTerminal: {
    bank: {
      adapterCode: string;
      id: number;
      name: string;
      paymentMethod: string;
    };
    id: number;
    name: string;
  };
  cardholderOverrideMinLength: string;
  cardholderOverrideType: CARDHOLDER_OVERRIDE_TYPES;
  checkStatusOnEarlyWebhook: boolean;
  checkStatusOnEarlyWebhookDelaySeconds: INullable<number>;
  customParametersDeposit: INullable<string>;
  customParametersPayout: INullable<string>;
  description: string;
  enabled: boolean;
  exitIFrame: boolean;
  exitIFrameMode: EXIT_IFRAME_MODE;
  hasSettlementConfig: boolean;
  id: number;
  limitsEnabled: boolean;
  merchantTerminal: {
    id: number;
    merchant: {
      id: number;
      name: string;
    };
    name: string;
  };
  motoEnabled: boolean;
  multipleFinalStates: boolean;
  payoutTimeout: INullable<number>;
  proxyUrl: string;
  pspSendPaymentTimeoutSeconds: INullable<number>;
  purchaseTimeout: INullable<number>;
  recurringEnabled: boolean;
  refundTimeout: INullable<number>;
  settlementContract: {
    contractName: string;
    id: number;
  };
  subPaymentMethod: INullable<string>;
  terminalLinkGroups: ITerminalLinkGroup[];
  threeDSEnabled: boolean;
  trusted: boolean;
  uniqueCustomerId: UNIQUE_CUSTOMER_ID_TYPES;
  useBankMessage: boolean;
  useCustomActions: boolean;
  useCustomReturnUrl: boolean;
  webhookIpWhitelistEnabled: boolean;
  webhookProxyUrl: INullable<string>;
  webhooksEnabled: boolean;
  whitelistIps: INullable<string[]>;
}

export type ITerminalsLinksData = TObject & {
  bankTerminal: TObject & {
    bank: TObject;
  };
  merchantTerminal: TObject & {
    merchant: TObject;
  };
  settlementContract: TObject;
  terminalLinkGroups: ITerminalLinkGroup[];
};

export interface ITLActionForm {
  accuracyPercentage?: INullable<number>;
  actionType?: INullable<string>;
  enabled: boolean;
  name?: INullable<string>;
  outputChannels?: number[];
  stage?: INullable<string>;
  terminalLink: {
    id: INullable<number>;
  };
  triggerType?: INullable<string>;
}

export interface ITLActionPayload {
  id?: number;
  payload: ITLActionForm;
}

export interface ITLGroupForm {
  enabled: boolean;
  id?: string;
  merchantId?: INullable<number>;
  name: INullable<string>;
}

export interface ITLGroupSettlementConfigurationsForm {
  parameters: ITLSettlementParameter[];
  search?: TSearch;
}

export interface ITLGroupTerminalLinksPayload {
  id: string;
  terminalLinks: (number | string)[];
}

export interface ITLGroupUniqueCustomerIdCounts {
  counts: IUniqueCustomerIdCount[];
  total: number;
}

export interface ITLLimitIssuingOrg {
  operation: ISSUING_ORG_OPERATION;
  valuesList: string[];
}

export interface ITLLimitPayload {
  id: string;
  payload: ITLLimitForm;
}

export interface ITLParameterLevelValue {
  hasEmptyValue: boolean;
  hasOwnValue: boolean;
  level: string;
  value: string;
}

export interface ITLParameterLowerLevelValues {
  dataType: string;
  levels: ITLParameterLevelValue[];
  name: string;
}

export interface ITLParametersNames {
  dataType: string;
  name: string;
  paramId: number;
}

export interface ITLSettlementConfig {
  endDate: INullable<string>;
  fixedTransactionFee: number;
  fixedTransactionFeeCcy: INullable<string>;
  forceFixedTransactionFeeCcy: boolean;
  id: INullable<number>;
  merchantDiscountRate: number;
  minTransactionFee: number;
  minTransactionFeeCcy: INullable<string>;
  rollingReserve: number;
  startDate: INullable<string>;
  terminalLink: {
    description: string;
    enabled: boolean;
    id: number;
    settlementContract: {
      contractName: string;
      id: number;
      settlementCurrency: string;
      settlementTimeZoneOffset: string;
    };
  };
  transactionCurrency: INullable<string>;
  transactionState: TRANSACTIONS_STATE;
  transactionType: TRANSACTIONS_TYPE;
}

export interface ITLSettlementConfigurations {
  configs: ITLSettlementConfig[];
  settlementContract: INullable<ITLSettlementContract>;
}

export interface ITLSettlementConfigurationsForm {
  parameters: ITLSettlementParameter[];
}

export interface ITLSettlementContract {
  contractName: string;
  contractStartDate: string;
  id: INullable<number>;
  merchant: {
    enabled: boolean;
    id: number;
    name: string;
  };
  paymentDelay: number;
  paymentPeriod: string;
  settlementCurrency: string;
  settlementTimeZoneOffset: string;
  terminalLinksCount: number;
}

export interface ITLSettlementParameter {
  endDate: INullable<string>;
  fixedTransactionFee: INullable<number>;
  fixedTransactionFeeCcy: INullable<string>;
  forceFixedTransactionFeeCcy: boolean;
  id?: INullable<number>;
  merchantDiscountRate: INullable<number>;
  minTransactionFee: INullable<number>;
  minTransactionFeeCcy: INullable<string>;
  rollingReserve: INullable<number>;
  startDate: INullable<string>;
  transactionCurrency: INullable<string>;
  transactionState?: INullable<TRANSACTIONS_STATE>;
  transactionType?: INullable<TRANSACTIONS_TYPE>;
}

export interface ITrustedSelectOption extends ISelectOption {
  limitsCount?: INullable<number>;
  merchant?: string;
  paymentMethod?: string;
  psp?: string;
  trusted: boolean;
  uniqueCustomerId?: INullable<UNIQUE_CUSTOMER_ID_TYPES>;
}

export interface IUniqueCustomerIdCount {
  count: number;
  uniqueCustomerId: INullable<UNIQUE_CUSTOMER_ID_TYPES>;
}

export type TTLActionData = TObject & {
  outputChannels: TObject[];
  terminalLink: TObject;
};

export type TTLSettlementConfigData = TObject & {
  terminalLink: TObject & {
    settlementContract: TObject;
  };
};

export type TTLSettlementContractData = TObject & {
  merchant: TObject;
};

type ILocaleDropDownValues = TObject & {
  attributes?: TObject;
  localData: ILocalDropDownValue[];
};

interface ILocalValidation {
  attributes?: TObject;
  defaultValue: INullable<string>;
  localData: INullable<ILocalDropDownValue[]>;
}
